import styled, { css, keyframes } from 'styled-components'

const BRAND = '#ef4781'
const BRAND_DARK = '#c9305f'
const BRAND_BLUE = '#58BFD6'
// Same as the Lanyard height: how much of the straps is on screen.
const LANYARD_DROP = 'max(110px, calc(50vh - 340px))'
const INK = '#111'
const TURN_DURATION = '1.5s'
// When the badge is edge-on: the face that was showing leaves the tab order.
const TURN_EDGE_DELAY = '0.6s'

// The card hangs from a lanyard like a badge ("crachá") and settles into place.
const swing = keyframes`
    0% { transform: rotate(-4deg); }
    40% { transform: rotate(2.5deg); }
    70% { transform: rotate(-1deg); }
    100% { transform: rotate(0deg); }
`

// Small sway of the whole badge when it settles after a turn, to the side it
// was turning; one animation per direction so each turn restarts it.
const settle = (side: 1 | -1) => keyframes`
    0% { transform: rotate(0deg); }
    30% { transform: rotate(${1.4 * side}deg); }
    65% { transform: rotate(${-0.7 * side}deg); }
    100% { transform: rotate(0deg); }
`
const settleToBack = settle(1)
const settleToFront = settle(-1)

const turnToBack = keyframes`
    0% { transform: rotateY(0deg); animation-timing-function: cubic-bezier(0.45, 0, 0.25, 1); }
    75% { transform: rotateY(188deg); animation-timing-function: ease-in-out; }
    88% { transform: rotateY(177deg); animation-timing-function: ease-in-out; }
    100% { transform: rotateY(180deg); }
`

const turnToFront = keyframes`
    0% { transform: rotateY(180deg); animation-timing-function: cubic-bezier(0.45, 0, 0.25, 1); }
    75% { transform: rotateY(-8deg); animation-timing-function: ease-in-out; }
    88% { transform: rotateY(3deg); animation-timing-function: ease-in-out; }
    100% { transform: rotateY(0deg); }
`

export const Container = styled.div`
    display: flex;
    justify-content: center;
    align-items: flex-start;
    min-height: 100vh;
    width: 100%;
    padding: 0 16px 48px;
    background-color: ${INK};
    background-image: radial-gradient(
        circle at 50% 40%,
        rgba(239, 71, 129, 0.16),
        transparent 55%
    );
    /* The lanyard straps run past the edges of the screen. */
    overflow-x: clip;
`

interface TurnProps {
    $flipped: boolean
    /** Whether the badge has been turned since the page opened. */
    $turned: boolean
}

export const Badge = styled.div<TurnProps>`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    max-width: 360px;
    transform-origin: top center;
    perspective: 1400px;
    animation: ${({ $turned, $flipped }) =>
        !$turned
            ? css`${swing} 1.6s ease-out both`
            : css`${$flipped ? settleToBack : settleToFront} 1.4s ease-out 1.1s both`};

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`

/** The two straps of the V, which stay around the neck while the badge turns. */
export const Lanyard = styled.div`
    position: relative;
    z-index: 3;
    width: 0;
    /* Hangs the card around the middle of the screen. */
    height: ${LANYARD_DROP};
`

export const Strap = styled.div<{ $side: 'left' | 'right' }>`
    position: absolute;
    bottom: -2px;
    left: 0;
    display: flex;
    justify-content: center;
    width: 40px;
    height: 160vh;
    overflow: hidden;
    transform-origin: bottom center;
    transform: translateX(-50%)
        rotate(${({ $side }) => ($side === 'left' ? '-24deg' : '24deg')});
    /* Woven stripes, darker edges, and the brand colors from pink at the reel
       to blue towards the neck, spread over the part that is on screen. */
    background: repeating-linear-gradient(
            0deg,
            transparent 0 9px,
            rgba(255, 255, 255, 0.08) 9px 11px
        ),
        linear-gradient(
            90deg,
            rgba(0, 0, 0, 0.18),
            transparent 22%,
            transparent 78%,
            rgba(0, 0, 0, 0.18)
        ),
        linear-gradient(0deg, ${BRAND} 0, ${BRAND_BLUE} calc(${LANYARD_DROP} * 1.15));
    box-shadow:
        inset 0 0 0 1px rgba(0, 0, 0, 0.18),
        0 0 12px rgba(0, 0, 0, 0.35);
    z-index: ${({ $side }) => ($side === 'left' ? 1 : 0)};

    span {
        writing-mode: vertical-rl;
        transform: rotate(180deg);
        white-space: nowrap;
        color: rgba(255, 255, 255, 0.55);
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.3em;
        line-height: 40px;
        user-select: none;
    }
`

/**
 * Everything below the V turns with the badge: the end of the lanyard that
 * twists, the clip and the card itself.
 */
export const Turner = styled.div<TurnProps>`
    position: relative;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    transform-style: preserve-3d;
    transform: rotateY(${({ $flipped }) => ($flipped ? '180deg' : '0deg')});
    animation: ${({ $turned, $flipped }) =>
        $turned
            ? css`${$flipped ? turnToBack : turnToFront} ${TURN_DURATION} both`
            : 'none'};

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`

const METAL = 'linear-gradient(90deg, #8e8e96, #f4f4f5 35%, #d4d4d8 60%, #8e8e96)'

/** Metal crimp where the two straps are sewn together; it hides their ends. */
export const Crimp = styled.div`
    position: absolute;
    bottom: -14px;
    left: 0;
    z-index: 2;
    width: 50px;
    height: 30px;
    transform: translateX(-50%);
    clip-path: polygon(0 0, 100% 0, 82% 100%, 18% 100%);
    background: ${METAL};
    box-shadow: inset 0 -4px 6px rgba(0, 0, 0, 0.25);

    /* Seam lines of the crimp. */
    &::after {
        content: '';
        position: absolute;
        inset: 8px 10px 8px;
        border-top: 1px solid rgba(0, 0, 0, 0.18);
        border-bottom: 1px solid rgba(0, 0, 0, 0.18);
    }
`

/** Split ring hanging from the crimp, holding the badge reel. */
export const Ring = styled.div`
    position: relative;
    z-index: 2;
    width: 22px;
    height: 22px;
    margin-top: 2px;
    border-radius: 50%;
    border: 3px solid #c4c4cc;
    box-shadow:
        inset 0 0 0 1px rgba(0, 0, 0, 0.15),
        0 1px 2px rgba(0, 0, 0, 0.4);
`

/**
 * Retractable badge reel ("roller clip") hanging from the ring. It stays still;
 * only the cord coming out of it turns with the badge.
 */
export const Reel = styled.div`
    position: relative;
    z-index: 4;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 60px;
    height: 60px;
    margin-top: -8px;
    border-radius: 50%;
    border: 4px solid #2b2b30;
    background: radial-gradient(circle at 35% 30%, #f7a3c0, ${BRAND} 45%, ${BRAND_DARK});
    box-shadow:
        inset 0 0 0 2px rgba(255, 255, 255, 0.25),
        0 4px 10px rgba(0, 0, 0, 0.5);
    color: #fff;
    font-size: 13px;
    font-weight: 800;
    letter-spacing: 0.08em;
    user-select: none;

    /* Tab the ring goes through. */
    &::before {
        content: '';
        position: absolute;
        top: -10px;
        left: 50%;
        width: 14px;
        height: 12px;
        border-radius: 3px 3px 0 0;
        transform: translateX(-50%);
        background: ${METAL};
        z-index: -1;
    }
`

/** The reel's cord, from the reel down to the vinyl strap. */
export const Cord = styled.div`
    width: 2px;
    height: 18px;
    margin-top: -4px;
    background: linear-gradient(90deg, #52525b, #a1a1aa, #52525b);
`

const strapLayer = css`
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 4px 4px 3px 3px;
    border: 1px solid rgba(255, 255, 255, 0.65);
    background:
        radial-gradient(circle, #e4e4e7 0 3px, #a1a1aa 3px 5px, transparent 5px)
            center 12px / 12px 12px no-repeat,
        linear-gradient(
            90deg,
            rgba(255, 255, 255, 0.35),
            rgba(255, 255, 255, 0.6) 50%,
            rgba(255, 255, 255, 0.35)
        );
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
`

/**
 * Clear vinyl strap with a snap, going from the ring into the badge slot.
 * One layer per side so it stays in front of the card when it is turned.
 */
export const ClipStrap = styled.div`
    position: relative;
    z-index: 1;
    width: 18px;
    height: 44px;
    margin-bottom: -26px;
    transform-style: preserve-3d;

    &::before {
        ${strapLayer}
        transform: translateZ(1px);
    }

    &::after {
        ${strapLayer}
        transform: rotateY(180deg) translateZ(1px);
    }
`

/** Holds both faces in the same grid cell so the card fits the taller one. */
export const CardStack = styled.div`
    display: grid;
    grid-template-columns: 100%;
    width: 100%;
    transform-style: preserve-3d;
`

const face = css<{ $active: boolean }>`
    grid-area: 1 / 1;
    position: relative;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 18px;
    /* ID-1 card in portrait: 54 x 86 mm. Grows if the content needs it. */
    aspect-ratio: 54 / 86;
    padding: 44px 28px 0;
    border-radius: 18px;
    overflow: hidden;
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
    box-shadow:
        0 24px 48px rgba(0, 0, 0, 0.55),
        0 2px 6px rgba(0, 0, 0, 0.3);
    /* The hidden face leaves the tab order once the card is half turned. */
    visibility: ${({ $active }) => ($active ? 'visible' : 'hidden')};
    transition: visibility 0s linear ${({ $active }) => ($active ? '0s' : TURN_EDGE_DELAY)};

    /* The slot the lanyard clip goes through. */
    &::before {
        content: '';
        position: absolute;
        top: 16px;
        left: 50%;
        width: 64px;
        height: 10px;
        border-radius: 5px;
        transform: translateX(-50%);
        background-color: ${INK};
    }

    @media (prefers-reduced-motion: reduce) {
        transition: none;
    }

    @media (max-width: 400px) {
        padding: 40px 20px 0;
    }
`

export const CardFront = styled.section<{ $active: boolean }>`
    ${face}
    background-color: #fff;
`

export const CardBack = styled.section<{ $active: boolean }>`
    ${face}
    transform: rotateY(180deg);
    background-color: #fafafa;
    background-image: linear-gradient(180deg, #fff, #f4f4f5);
`

/** Single-sided card, for screens that do not flip. */
export const LoginCard = styled.section`
    ${face}
    background-color: #fff;
`

export const LogoContainer = styled.div<{ $compact?: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${({ $compact }) => ($compact ? '10px' : '18px')};
    padding-top: ${({ $compact }) => ($compact ? '0' : '16px')};
`

export const Logo = styled.img<{ $compact?: boolean }>`
    width: ${({ $compact }) => ($compact ? '45%' : '78%')};
    max-width: 240px;
    object-fit: contain;
`

export const Heading = styled.div`
    text-align: center;

    h1 {
        margin: 0;
        font-size: 20px;
        font-weight: 600;
        color: ${INK};
    }

    p {
        margin: 4px 0 0;
        font-size: 14px;
        color: #6b6b6b;
    }
`

export const Actions = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    justify-content: center;
    gap: 14px;
    width: 100%;
`

export const Button = styled.button`
    width: 100%;
    min-height: 44px;
    padding: 10px 20px;
    border: none;
    border-radius: 6px;
    background-color: ${BRAND};
    color: #fff;
    font-size: 15px;
    font-weight: 500;
    cursor: pointer;
    transition: opacity 0.2s ease-in-out;

    &:hover {
        opacity: 0.88;
    }

    &:focus-visible {
        outline: 2px solid ${INK};
        outline-offset: 2px;
    }
`

/** Light theme of Microsoft's sign-in button guidelines. */
export const MicrosoftButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    width: 100%;
    min-height: 48px;
    padding: 0 16px;
    border: 1px solid #8c8c8c;
    border-radius: 6px;
    background-color: #fff;
    color: #5e5e5e;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition:
        background-color 0.2s ease-in-out,
        border-color 0.2s ease-in-out;

    &:hover {
        background-color: #f4f4f4;
        border-color: #5e5e5e;
    }

    &:focus-visible {
        outline: 2px solid ${BRAND};
        outline-offset: 2px;
    }

    svg {
        flex-shrink: 0;
    }
`

export const TextButton = styled.button`
    align-self: center;
    padding: 4px 8px;
    border: none;
    background: none;
    color: #6b6b6b;
    font-size: 13px;
    text-decoration: underline;
    text-underline-offset: 3px;
    cursor: pointer;

    &:hover {
        color: ${INK};
    }

    &:focus-visible {
        outline: 2px solid ${BRAND};
        outline-offset: 2px;
        border-radius: 4px;
    }
`

export const EmailPasswordContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    width: 100%;
`

export const LoginNotice = styled.p`
    width: 100%;
    margin: 0;
    font-size: 14px;
    color: #555;
    text-align: center;
`

export const BadgeFooter = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 -28px;
    padding: 16px;
    background-color: ${BRAND};
    color: #fff;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.18em;
    text-transform: uppercase;

    a {
        color: #fff;
        letter-spacing: normal;
        text-transform: none;
    }

    @media (max-width: 400px) {
        margin: 0 -20px;
    }
`
